// Arşiv liste ekranı — plan §4.6.1.
// Tamamlanmış projeler + multi-select sektör/yıl/müşteri filter + pagination.
// Raporlama dönemi (spec 2C): dönem TEKİL daraltma — ikinci tık seçimi kaldırır.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  formatReportingPeriod,
  type ArchiveFilterOptionsResponse,
  type ArchiveListItem,
  type ArchiveListResponse,
  type PeriodType,
} from "@mahir/shared";
import { api } from "../api/client";
import { searchClients } from "../api/clients";
import { ClientMultiPicker } from "../components/common/ClientMultiPicker";
import { EmptyState, ErrorState, LoadingState } from "../components/common/StateSurface";
import type { ClientPickerItem } from "../lib/client-picker";
import { ImportedPill } from "../components/report-import/ImportedPill";
import { PERIOD_COPY, periodFilterOptions } from "../lib/period-ui";
import { IMPORT_COPY } from "../lib/report-import-ui";

export function ArchiveList(): JSX.Element {
  const [items, setItems] = useState<ArchiveListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [filterOptions, setFilterOptions] = useState<ArchiveFilterOptionsResponse | null>(null);
  // Sektör + müşteri ClientMultiPicker (2026-10-07): chip bulutu yerine aramalı combobox.
  // Sektör yerel (küçük liste, sunucuya gitmez); müşteri boş sorguda arşivin ilk 50'si +
  // toplam, yazınca `GET /clients?search=`. Seçili müşteri id+ad taşır (çip).
  const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedClientItems, setSelectedClientItems] = useState<ClientPickerItem[]>([]);
  const selectedClients = useMemo(
    () => selectedClientItems.map((c) => c.id),
    [selectedClientItems],
  );
  // Dönem tekil: boş string = tümü (ProjectsList "Dönem (tümü)" deseniyle aynı).
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType | "">("");
  // Geçmiş Rapor Aktarımı 4A/R13 — "" (tümü) | "import" | "manual".
  const [selectedSource, setSelectedSource] = useState<"" | "import" | "manual">("");
  const [search, setSearch] = useState("");

  const pageSize = 25;

  const buildQuery = useCallback((): string => {
    const p = new URLSearchParams();
    p.set("page", page.toString());
    p.set("limit", pageSize.toString());
    if (search.trim()) p.set("search", search.trim());
    if (selectedSectors.length > 0) p.set("sectors", selectedSectors.join(","));
    if (selectedYears.length > 0) p.set("years", selectedYears.map((y) => y.toString()).join(","));
    if (selectedClients.length > 0) p.set("clientIds", selectedClients.join(","));
    if (selectedPeriod) p.set("periodType", selectedPeriod);
    if (selectedSource) p.set("source", selectedSource);
    return p.toString();
  }, [
    page,
    search,
    selectedSectors,
    selectedYears,
    selectedClients,
    selectedPeriod,
    selectedSource,
  ]);

  // İlk yüklemede filter options'ı getir
  useEffect(() => {
    api<ArchiveFilterOptionsResponse>("/archive/filters")
      .then((opts) => setFilterOptions(opts))
      .catch(() => {
        // ignore
      });
  }, []);

  // Liste fetch
  useEffect(() => {
    let aborted = false;
    setLoading(true);
    api<ArchiveListResponse>(`/archive?${buildQuery()}`)
      .then((res) => {
        if (aborted) return;
        setItems(res.items);
        setTotal(res.total);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Arşiv yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [buildQuery, refreshTick]);

  const toggle = <T,>(arr: T[], setter: (v: T[]) => void, v: T): void => {
    setPage(1);
    setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  };

  const sectorOptions = useMemo<ClientPickerItem[]>(
    () => (filterOptions?.sectors ?? []).map((sec) => ({ id: sec, name: sec })),
    [filterOptions],
  );
  const selectedSectorItems = useMemo<ClientPickerItem[]>(
    () => selectedSectors.map((sec) => ({ id: sec, name: sec })),
    [selectedSectors],
  );
  // Boş sorgu: tamamlanmış projesi olan müşteriler (ilk 50 + toplam); yazınca sunucu araması.
  const fetchArchiveClients = useCallback(
    async (query: string): Promise<{ items: ClientPickerItem[]; total: number }> => {
      if (query.trim()) return searchClients(query);
      return {
        items: filterOptions?.clients ?? [],
        total: filterOptions?.clientsTotal ?? filterOptions?.clients.length ?? 0,
      };
    },
    [filterOptions],
  );

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total]);

  // Arama da bir daraltmadır (ProjectsList anyFilter deseni): yalnız arama yazılıp
  // sonuç çıkmadığında da "filtreyle eşleşmedi" dalı doğru olmalı.
  // G12 — `anyFilter` ve `clearFilters` aynı alan kümesini tanır (`source` dahil).
  const anyFilter =
    selectedSectors.length + selectedYears.length + selectedClients.length > 0 ||
    selectedPeriod !== "" ||
    selectedSource !== "" ||
    search.trim().length > 0;
  const clearFilters = (): void => {
    setPage(1);
    setSelectedSectors([]);
    setSelectedYears([]);
    setSelectedClientItems([]);
    setSelectedPeriod("");
    setSelectedSource("");
    setSearch("");
  };

  return (
    <div className="p-8" style={{ fontFamily: "var(--font-sans)", maxWidth: 1400 }}>
      <div
        className="text-xs uppercase tracking-wider mb-2"
        style={{ color: "var(--color-text-3)" }}
      >
        ARŞİV
      </div>
      <h1
        className="mb-4"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: "1.8rem",
          color: "var(--color-text)",
        }}
      >
        Tamamlanmış Projeler
      </h1>
      <p className="text-sm mb-6" style={{ color: "var(--color-text-2)" }}>
        {total} proje — arşiv salt-okunur. Döküman versiyonları proje sayfasında görünür.
      </p>

      {/* Filters */}
      <div
        className="mb-4 p-4 rounded-lg"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
          placeholder="Müşteri veya çerçeve ara…"
          className="w-full px-3 py-2 rounded border text-sm mb-3"
          style={{ borderColor: "var(--color-border)" }}
        />

        {filterOptions && (
          <div className="grid gap-3" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
            <div>
              <div
                className="text-xs mb-1 uppercase tracking-wider"
                style={{ color: "var(--color-text-3)" }}
              >
                Sektör
              </div>
              <ClientMultiPicker
                mode="local"
                options={sectorOptions}
                values={selectedSectorItems}
                ariaLabel="Sektör"
                placeholder="Sektör ara…"
                size="sm"
                onChange={(items) => {
                  setPage(1);
                  setSelectedSectors(items.map((i) => i.id));
                }}
              />
            </div>
            <FilterGroup
              label="Yıl"
              options={filterOptions.years.map((y) => ({
                value: y.toString(),
                label: y.toString(),
              }))}
              selected={selectedYears.map((y) => y.toString())}
              onToggle={(v) => toggle(selectedYears, setSelectedYears, parseInt(v, 10))}
            />
            <FilterGroup
              label={PERIOD_COPY.columnLabel}
              options={periodFilterOptions()}
              selected={selectedPeriod ? [selectedPeriod] : []}
              onToggle={(v) => {
                setPage(1);
                setSelectedPeriod((prev) => (prev === v ? "" : (v as PeriodType)));
              }}
            />
            <div>
              <div
                className="text-xs mb-1 uppercase tracking-wider"
                style={{ color: "var(--color-text-3)" }}
              >
                Müşteri
              </div>
              <ClientMultiPicker
                fetchOptions={fetchArchiveClients}
                values={selectedClientItems}
                ariaLabel="Müşteri"
                size="sm"
                allLink="/clients"
                onChange={(items) => {
                  setPage(1);
                  setSelectedClientItems(items);
                }}
              />
            </div>
            {/* 4A — Kaynak (Aktarım / Elle). Dört sütunluk grid'de beşinci grup
                ikinci satıra sarar (G11 kabul). Tek seçim: aynı değere tekrar
                basmak "tümü"ne döner (Dönem grubuyla aynı davranış). */}
            <FilterGroup
              label={IMPORT_COPY.sourceFilterLabel.replace(` ${IMPORT_COPY.filterAllSuffix}`, "")}
              options={[
                { value: "import", label: IMPORT_COPY.sourceFilterImport },
                { value: "manual", label: IMPORT_COPY.sourceFilterManual },
              ]}
              selected={selectedSource ? [selectedSource] : []}
              onToggle={(v) => {
                setPage(1);
                setSelectedSource((prev) => (prev === v ? "" : (v as "import" | "manual")));
              }}
            />
          </div>
        )}

        {anyFilter && (
          <button
            onClick={clearFilters}
            className="mt-3 text-xs"
            style={{ color: "var(--color-primary)" }}
          >
            {PERIOD_COPY.clearFilters}
          </button>
        )}
      </div>

      {/* Table */}
      <div
        className="rounded-lg overflow-hidden"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        {/* Boş durum FİLTREYE bağlı, `total`a değil: filtre değişince sayfa 1'e
            dönüldüğü için items boşken total da 0'dır — `total === 0` dalı
            "filtreyle eşleşmedi" halini hiç yakalayamazdı (ProjectsList deseni). */}
        {error && items.length === 0 ? (
          <ErrorState message={error} onRetry={() => setRefreshTick((t) => t + 1)} />
        ) : loading && items.length === 0 ? (
          <LoadingState />
        ) : items.length === 0 ? (
          <EmptyState
            title={
              anyFilter
                ? selectedPeriod
                  ? PERIOD_COPY.listEmptyPeriod
                  : "Filtreyle eşleşen sonuç yok"
                : "Henüz tamamlanmış proje yok"
            }
            hint={
              anyFilter
                ? PERIOD_COPY.archiveEmptyHint
                : "Proje tamamlanıp docx ihraç edildikten sonra burada görünür."
            }
            {...(anyFilter
              ? {
                  action: (
                    <button
                      onClick={clearFilters}
                      className="text-xs"
                      style={{ color: "var(--color-primary)" }}
                    >
                      {PERIOD_COPY.clearFilters}
                    </button>
                  ),
                }
              : {})}
          />
        ) : null}
        {items.length > 0 && (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--color-bg)" }}>
                <th
                  className="text-left px-4 py-2"
                  style={{ color: "var(--color-text-3)", fontWeight: 600 }}
                >
                  Müşteri
                </th>
                <th
                  className="text-left px-4 py-2"
                  style={{ color: "var(--color-text-3)", fontWeight: 600 }}
                >
                  Sektör
                </th>
                <th
                  className="text-left px-4 py-2"
                  style={{ color: "var(--color-text-3)", fontWeight: 600 }}
                >
                  Çerçeve
                </th>
                <th
                  className="text-left px-4 py-2"
                  style={{ color: "var(--color-text-3)", fontWeight: 600 }}
                >
                  {PERIOD_COPY.columnLabel}
                </th>
                <th
                  className="text-left px-4 py-2"
                  style={{ color: "var(--color-text-3)", fontWeight: 600 }}
                >
                  Danışman
                </th>
                <th
                  className="text-right px-4 py-2"
                  style={{ color: "var(--color-text-3)", fontWeight: 600 }}
                >
                  Tamamlandı
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => (
                <tr key={i.id} className="hover:bg-[rgba(0,0,0,0.02)]">
                  <td className="px-4 py-3" style={{ borderTop: "1px solid var(--color-border)" }}>
                    <Link
                      to={`/archive/${i.id}`}
                      style={{ color: "var(--color-primary)", fontWeight: 600 }}
                    >
                      {i.clientName}
                    </Link>
                    {/* 4A — kolon EKLENMEZ; nötr pill müşteri adının yanında durur. */}
                    {i.hasImport === true ? (
                      <>
                        {" "}
                        <ImportedPill />
                      </>
                    ) : null}
                  </td>
                  <td
                    className="px-4 py-3"
                    style={{
                      borderTop: "1px solid var(--color-border)",
                      color: "var(--color-text-2)",
                    }}
                  >
                    {i.clientSector ?? "—"}
                  </td>
                  <td
                    className="px-4 py-3"
                    style={{
                      borderTop: "1px solid var(--color-border)",
                      color: "var(--color-text-2)",
                    }}
                  >
                    {i.frameworkName} · {i.frameworkVersion}
                  </td>
                  <td
                    className="px-4 py-3"
                    style={{
                      borderTop: "1px solid var(--color-border)",
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {formatReportingPeriod(i.reportingYear, i.periodType)}
                  </td>
                  <td
                    className="px-4 py-3"
                    style={{
                      borderTop: "1px solid var(--color-border)",
                      color: "var(--color-text-2)",
                    }}
                  >
                    {i.assignedToName}
                  </td>
                  <td
                    className="px-4 py-3 text-right"
                    style={{
                      borderTop: "1px solid var(--color-border)",
                      color: "var(--color-text-3)",
                    }}
                  >
                    {new Date(i.completedAt).toLocaleDateString("tr-TR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {total > pageSize && (
        <div
          className="mt-4 flex justify-between items-center text-sm"
          style={{ color: "var(--color-text-2)" }}
        >
          <span>
            Sayfa {page} / {totalPages} — toplam {total} proje
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: "var(--color-border)" }}
            >
              Önceki
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 rounded border disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderColor: "var(--color-border)" }}
            >
              Sonraki
            </button>
          </div>
        </div>
      )}

      {/* M9-S2: error artık items.length === 0 durumunda ErrorState surface'ıyla render;
          items doluyken sayfayı bozmasın diye sessiz (console'dan takip edilir). */}
    </div>
  );
}

function FilterGroup({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (v: string) => void;
}): JSX.Element {
  return (
    <div>
      <div
        className="text-xs mb-1 uppercase tracking-wider"
        style={{ color: "var(--color-text-3)" }}
      >
        {label}
      </div>
      <div className="flex flex-wrap gap-1">
        {options.length === 0 && (
          <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
            —
          </span>
        )}
        {options.map((opt) => {
          const active = selected.includes(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => onToggle(opt.value)}
              className="px-2 py-1 rounded text-xs"
              style={{
                background: active ? "var(--color-primary)" : "var(--color-bg)",
                color: active ? "var(--color-surface)" : "var(--color-text-2)",
                border: "1px solid var(--color-border)",
                fontWeight: active ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
